
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { Menu, X, ArrowRight } from 'lucide-react';

const Navbar: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    const links = [
        { name: 'About', path: '/about' },
        { name: 'Contact', path: '/contact' },
    ];

    const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);
    const [isLearnOpen, setIsLearnOpen] = useState(false);

    return (
        <nav className="fixed w-full bg-white/80 backdrop-blur-md z-50 border-b border-sage-100 no-print">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <Link to="/" className="flex-shrink-0 flex items-center">
                        <Logo className="h-8 w-8" textClassName="text-xl" />
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center space-x-8">
                        <Link to="/about" className="text-sage-700 hover:text-sage-900 font-medium transition-colors">About</Link>

                        {/* Features Dropdown */}
                        <div className="relative group">
                            <button
                                className="text-sage-700 hover:text-sage-900 font-medium transition-colors flex items-center gap-1"
                                onClick={() => setIsFeaturesOpen(!isFeaturesOpen)}
                                onMouseEnter={() => setIsFeaturesOpen(true)}
                            >
                                Features
                            </button>
                            <div
                                className="absolute top-full left-0 w-48 bg-white border border-sage-100 rounded-xl shadow-lg py-2 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                                onMouseEnter={() => setIsFeaturesOpen(true)}
                                onMouseLeave={() => setIsFeaturesOpen(false)}
                            >
                                <Link to="/who-its-for" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">Who It's For</Link>
                                <Link to="/" onClick={() => setTimeout(() => document.getElementById('healing-toolkit')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">Healing Toolkit</Link>
                                <Link to="/plan-comparison" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">Plan Comparison</Link>
                                <Link to="/faq" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">FAQ</Link>
                            </div>
                        </div>

                        <Link to="/contact" className="text-sage-700 hover:text-sage-900 font-medium transition-colors">Contact</Link>

                        {/* Learn Dropdown */}
                        <div className="relative group">
                            <button
                                className="text-sage-700 hover:text-sage-900 font-medium transition-colors flex items-center gap-1"
                                onClick={() => setIsLearnOpen(!isLearnOpen)}
                                onMouseEnter={() => setIsLearnOpen(true)}
                            >
                                Learn
                            </button>
                            <div
                                className="absolute top-full right-0 w-64 bg-white border border-sage-100 rounded-xl shadow-lg py-2 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                                onMouseEnter={() => setIsLearnOpen(true)}
                                onMouseLeave={() => setIsLearnOpen(false)}
                            >
                                <Link to="/learn/what-is-ayurveda" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">What Is Ayurveda?</Link>
                                <Link to="/learn/what-is-naturopathy" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">What Is Naturopathy?</Link>
                                <Link to="/learn/what-is-acupressure" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">What Is Acupressure?</Link>
                                <Link to="/learn/what-is-traditional-chinese-medicine" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">What Is TCM?</Link>
                                <Link to="/learn/what-is-homeopathy" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">What Is Homeopathy?</Link>
                                <Link to="/learn/ancient-healing-traditions-compared" className="block px-4 py-2 text-sage-700 hover:bg-sage-50 hover:text-sage-900 text-sm">All Five Compared</Link>
                            </div>
                        </div>

                        <Link
                            to="/app"
                            className="bg-sage-600 text-white px-5 py-2.5 rounded-full font-bold hover:bg-sage-700 transition-all transform hover:scale-105 shadow-md flex items-center gap-2"
                        >
                            Launch App <ArrowRight size={18} />
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="text-sage-700 hover:text-sage-900 p-2"
                        >
                            {isOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isOpen && (
                <div className="md:hidden bg-white border-t border-sage-100">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                        <Link to="/about" onClick={() => setIsOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-sage-700 hover:bg-sage-50 hover:text-sage-900">About</Link>
                        <div className="px-3 py-2">
                            <div className="font-medium text-sage-900 mb-2">Features</div>
                            <Link to="/who-its-for" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">Who It's For</Link>
                            <Link to="/" onClick={() => { setIsOpen(false); setTimeout(() => document.getElementById('healing-toolkit')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">Healing Toolkit</Link>
                            <Link to="/plan-comparison" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">Plan Comparison</Link>
                            <Link to="/faq" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">FAQ</Link>
                        </div>
                        <Link to="/contact" onClick={() => setIsOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-sage-700 hover:bg-sage-50 hover:text-sage-900">Contact</Link>

                        <div className="px-3 py-2">
                            <div className="font-medium text-sage-900 mb-2">Learn</div>
                            <Link to="/learn/what-is-ayurveda" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">What Is Ayurveda?</Link>
                            <Link to="/learn/what-is-naturopathy" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">What Is Naturopathy?</Link>
                            <Link to="/learn/what-is-acupressure" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">What Is Acupressure?</Link>
                            <Link to="/learn/what-is-traditional-chinese-medicine" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">What Is TCM?</Link>
                            <Link to="/learn/what-is-homeopathy" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">What Is Homeopathy?</Link>
                            <Link to="/learn/ancient-healing-traditions-compared" onClick={() => setIsOpen(false)} className="block pl-4 py-2 text-sm text-sage-600 hover:text-sage-900 border-l border-sage-200">All Five Compared</Link>
                        </div>

                        <Link
                            to="/app"
                            onClick={() => setIsOpen(false)}
                            className="block w-full text-center mt-4 bg-sage-600 text-white px-5 py-3 rounded-xl font-bold hover:bg-sage-700 transition-colors"
                        >
                            Launch App
                        </Link>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
